"use client";

import {
  m as motion,
  useInView,
  useReducedMotion,
  useMotionValue,
  useTransform,
  animate,
  type MotionValue,
} from "framer-motion";
import { useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * "Come funziona" come flusso orizzontale di un task (4 regole / step):
 * 1 task attivo -> comunicazione asincrona -> consegna pronta -> revisioni.
 *
 * Animazione a CIRCUITO continuo (SPEC ANIMAZIONE CIRCUITO):
 *  - Un SINGOLO valore di `progress` (motionValue) gira 0 -> 1 in loop. Da esso
 *    derivano, via `useTransform`, SIA la posizione della barretta in avanti SIA
 *    il glow di ogni nodo: il nodo i si accende esattamente quando la barretta lo
 *    tocca (sync stretto, nessuna deriva possibile).
 *  - Fase avanti `[0, FORWARD_END]`: una barretta CORTA e VELOCE scorre da
 *    centro-icona-1 a centro-icona-4 accendendo i nodi al passaggio.
 *  - Fase ritorno `(FORWARD_END, 1]`: un pallino torna dall'ultima alla prima
 *    icona in modo FLUIDO e CONTINUO lungo un arco, visibile per tutto il
 *    tragitto (niente pause, niente sparizioni a meta').
 *  - La barretta in avanti riparte solo quando il pallino ha toccato la prima
 *    icona (il loop e' un unico progress, quindi il ciclo e' pulito per
 *    costruzione: avanti -> ritorno -> tocca la prima -> riparte).
 *
 * Geometria: connettore da centro icona 1 a centro icona 4 della riga. Le icone
 * sono allineate a SINISTRA delle colonne (grid-cols-4 gap-5, icona w-[4.5rem]),
 * quindi inset left = 2.25rem (meta' icona) e inset right =
 * calc((100% - 3*1.25rem)/4 - 2.25rem). Tutto dentro al container. Solo
 * transform/opacity.
 *
 * Gate: `loopActive = inView && !reduce` (useInView SENZA once). Fuori schermo o
 * con reduced-motion -> stato statico acceso (nessun loop).
 */

export interface FlowStep {
  icon: LucideIcon;
  index: string;
  title: string;
  descriptionTop: string;
  descriptionBottom: string;
}

interface TaskFlowProps {
  isItalian: boolean;
  steps: FlowStep[];
}

// Durata di un ciclo completo del circuito (in secondi). Piu' breve = piu' vivo.
const LOOP_DURATION = 4.2;
// Frazione del ciclo dedicata al viaggio in avanti L->R (il resto e' l'arco di ritorno).
const FORWARD_END = 0.62;
// Mezza ampiezza (in frazione di progress) della "finestra" di accensione di un nodo.
const GLOW_HALF_WIDTH = 0.07;

/**
 * Nodo del flusso. Possiede il proprio valore di glow (0..1) derivato dal
 * progress CONDIVISO: il nodo i si accende esattamente quando la barretta lo
 * tocca. Lo stesso valore pilota sia il layer di glow dietro l'icona sia la
 * "vivacita'" di icona+titolo (label in sync con l'icona). Solo opacity/color.
 */
function FlowNode({
  step,
  index,
  total,
  progress,
  loopActive,
  reduce,
  show,
}: {
  step: FlowStep;
  index: number;
  total: number;
  progress: MotionValue<number>;
  loopActive: boolean;
  reduce: boolean | null;
  show: boolean;
}) {
  // Posizione (in progress) in cui la barretta tocca questo nodo, dentro la
  // fase di avanti [0, FORWARD_END].
  const center = total > 1 ? (index / (total - 1)) * FORWARD_END : 0;

  // Valore di accensione 0 (riposo) -> 1 (al passaggio della barretta).
  const lit = useTransform(progress, (p) => {
    if (!loopActive) return reduce ? 1 : 0;
    const d = Math.abs(p - center);
    if (d >= GLOW_HALF_WIDTH) return 0;
    const t = 1 - d / GLOW_HALF_WIDTH; // 0 al bordo, 1 al centro
    return t * t * (3 - 2 * t); // smoothstep
  });

  // Derivazioni: glow layer (opacity), titolo (da muted a primary via opacity di
  // un overlay ciano sopra il testo base), bordo/scale leggera dell'icona.
  const glowOpacity = useTransform(lit, [0, 1], [0.18, 1]);
  const titleAccentOpacity = lit; // 0..1
  const iconScale = useTransform(lit, [0, 1], [1, 1.06]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={show ? { opacity: 1, y: 0 } : {}}
      transition={
        reduce ? { duration: 0 } : { duration: 0.5, delay: 0.15 * index }
      }
      className="relative flex flex-col items-start text-left"
    >
      {/* Nodo icona sulla linea: allineato a SINISTRA (segue items-start del
          contenitore), così sta in alto-a-sinistra del nodo, sul bordo sinistro
          del testo left-aligned. Spazio confortevole sotto verso il testo. */}
      <div className="relative z-10 mb-6 self-center sm:self-start">
        {/* Glow layer: si accende quando la barretta tocca il nodo (opacity). */}
        <motion.span
          aria-hidden="true"
          className="absolute -inset-2 rounded-3xl bg-primary/55 blur-xl"
          style={{ opacity: glowOpacity }}
        />
        <motion.div
          style={{ scale: iconScale }}
          className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl border border-primary/25 bg-surface shadow-[0_8px_28px_hsl(var(--primary)/0.12)]"
        >
          <step.icon className="h-6 w-6 text-primary" />
          <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
            {step.index}
          </span>
        </motion.div>
      </div>

      {/* Titolo: base muted + overlay ciano che si accende in sync con l'icona. */}
      <h3 className="relative w-full font-display text-lg font-semibold mb-2 text-left">
        <span className="text-foreground">{step.title}</span>
        <motion.span
          aria-hidden="true"
          style={{ opacity: titleAccentOpacity }}
          className="pointer-events-none absolute inset-0 text-primary"
        >
          {step.title}
        </motion.span>
      </h3>
      <p className="w-full text-sm text-muted-foreground leading-relaxed text-left">
        {step.descriptionTop}
      </p>
      <p className="mt-2 w-full text-sm leading-relaxed text-primary text-left">
        {step.descriptionBottom}
      </p>
    </motion.div>
  );
}

export function TaskFlow({ isItalian, steps }: TaskFlowProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  // Reveal one-shot allo scroll (entrata cards).
  const inViewOnce = useInView(ref, { once: true, margin: "-80px" });
  // Gate del loop: continuo, si spegne quando la sezione esce dal viewport.
  const inViewLoop = useInView(ref, { margin: "-80px" });
  const show = inViewOnce || Boolean(reduce);
  const loopActive = inViewLoop && !reduce;

  const n = steps.length; // 4

  // Singolo valore di progress 0 -> 1, sorgente di TUTTO (barretta + glow + dot).
  const progress = useMotionValue(0);

  useEffect(() => {
    if (!loopActive) {
      progress.set(0);
      return;
    }
    const controls = animate(progress, 1, {
      duration: LOOP_DURATION,
      ease: "linear",
      repeat: Infinity,
    });
    return () => controls.stop();
  }, [loopActive, progress]);

  // --- Barretta in avanti -------------------------------------------------
  // x in % lungo il connettore: 0% (icona 1) -> 100% (icona 4) durante la fase
  // avanti; tenuta a 100% e nascosta durante il ritorno.
  const barX = useTransform(progress, [0, FORWARD_END, 1], ["0%", "100%", "100%"]);
  const barOpacity = useTransform(
    progress,
    [0, FORWARD_END * 0.04, FORWARD_END * 0.96, FORWARD_END, 1],
    [0, 1, 1, 0, 0],
  );

  // --- Pallino di ritorno (arco continuo 4 -> 1) --------------------------
  // x: da 100% (icona 4) a 0% (icona 1) in modo monotono e continuo sull'intera
  // fase di ritorno. y: dip verso l'alto a meta' arco. Visibile per tutto il
  // tragitto, fade-in/out brevissimo solo agli estremi per non "spuntare" netto.
  const r0 = FORWARD_END;
  const dotX = useTransform(
    progress,
    [r0, 1],
    ["100%", "0%"],
  );
  const dotY = useTransform(
    progress,
    [r0, (r0 + 1) / 2, 1],
    [0, -36, 0],
  );
  const dotOpacity = useTransform(
    progress,
    [r0, r0 + 0.04, 0.97, 1],
    [0, 1, 1, 0],
  );

  return (
    <section className="py-20 md:py-28 lg:py-28 bg-surface/30">
      <div className="container-wide" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={show ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12 text-left"
        >
          <span className="font-mono text-label text-primary mb-4 block">
            {isItalian ? "Il flusso di un task" : "A task flow"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? "Come funziona" : "How it works"}
          </h2>
          <p className="text-body-lg text-muted-foreground max-w-xl">
            {isItalian
              ? "Regole semplici, risultati prevedibili."
              : "Simple rules, predictable results."}
          </p>
        </motion.div>

        <div className="relative mx-auto max-w-5xl">
          {/* Connettore (desktop): da centro icona 1 a centro icona 4.
              top-[2.25rem] = meta' verticale del nodo (h-[4.5rem] = 72px).
              Icone ora allineate a SINISTRA delle colonne (grid-cols-4 gap-5,
              gap 1.25rem, icona w-[4.5rem]). Quindi:
              - left = 2.25rem (meta' icona): centro icona 1.
              - right = colonna - 2.25rem = calc((100% - 3*1.25rem)/4 - 2.25rem):
                porta l'estremo destro del binario sul centro icona 4.
              Cosi' il binario parte ESATTAMENTE dal centro icona 1 e finisce sul
              centro icona 4, senza sporgere a destra o a sinistra. I due carrier
              animati (barretta + pallino) ereditano questi stessi bound via
              left-0/right-0, restando in sync con i centri icona. */}
          <div className="pointer-events-none absolute left-[2.25rem] right-[calc((100%-3*1.25rem)/4-2.25rem)] top-[2.25rem] hidden lg:block">
            {/* Binario di sfondo */}
            <div className="absolute inset-x-0 top-0 h-px -translate-y-1/2 bg-[hsl(var(--border))]" />
            {/* Binario che si riempie (reveal allo scroll) */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={show ? { scaleX: 1 } : {}}
              transition={
                reduce
                  ? { duration: 0 }
                  : { duration: 1.2, delay: 0.2, ease: [0.22, 0.7, 0.2, 1] }
              }
              style={{ transformOrigin: "left center" }}
              className="absolute inset-x-0 top-0 h-px -translate-y-1/2 bg-gradient-to-r from-primary via-primary to-primary/40"
            />

            {/* Barretta "elettrica" CORTA che scorre L->R, derivata dal progress
                condiviso (sync 1:1 con l'accensione dei nodi). Solo transform.
                Spark ancorata a `left-0`: a x=0% sta sul centro icona 1, a x=100%
                sul centro icona 4. La barX traduce 0% -> 100% lungo il binario e
                l'opacity svanisce a FORWARD_END (icona 4): nulla viene disegnato a
                destra dell'icona 4. Il `-translate-x-1/2` tiene la barretta
                centrata sull'estremo, senza sporgere oltre. */}
            {loopActive && (
              <motion.div
                className="absolute top-0 left-0 right-0 -translate-y-1/2"
                style={{ x: barX, opacity: barOpacity }}
              >
                <span className="absolute left-0 top-0 h-[3px] w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.8)]" />
              </motion.div>
            )}

            {/* Pallino di ritorno a semicerchio (4 -> 1): un solo wrapper con
                x/y/opacity continui dal progress condiviso, niente keyframe a
                scatti. Visibile per tutto il tragitto. Solo transform/opacity. */}
            {loopActive && (
              <motion.div
                className="absolute top-0 left-0 right-0"
                style={{ x: dotX }}
              >
                <motion.div
                  className="absolute top-0 left-0 -ml-[5px] -mt-[5px] h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_12px_hsl(var(--primary)/0.7)]"
                  style={{ y: dotY, opacity: dotOpacity }}
                />
              </motion.div>
            )}
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, index) => (
              <FlowNode
                key={step.title}
                step={step}
                index={index}
                total={n}
                progress={progress}
                loopActive={loopActive}
                reduce={reduce}
                show={show}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
